import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { attributionColumns, attributionFields } from "@/lib/attribution-schema";

const grannyFlatSchema = z.object({
  name: z.string().trim().min(2, "Please tell us your name").max(120),
  email: z.string().trim().email("Please enter a valid email address").max(200),
  phone: z.string().trim().max(40).optional().or(z.literal("")),
  relationship: z.enum([
    "older-person",
    "adult-child",
    "other-family",
    "attorney-or-carer",
    "adviser",
  ]),
  ageRange: z.enum(["under-60", "60-69", "70-79", "80-plus", "not-applicable"]),
  fundingSource: z.enum([
    "sale-of-home",
    "savings",
    "superannuation",
    "inheritance",
    "loan-or-mortgage",
    "not-decided",
  ]),
  contributionAmount: z.string().trim().max(60).optional().or(z.literal("")),
  propertySuburb: z.string().trim().max(120).optional().or(z.literal("")),
  meetingPreference: z.enum(["in-person", "phone", "video"]),
  message: z.string().trim().max(4000).optional().or(z.literal("")),
  sourcePage: z.string().trim().max(200).optional().or(z.literal("")),
  ...attributionFields,
});

export type GrannyFlatEnquiryInput = z.infer<typeof grannyFlatSchema>;

export const submitGrannyFlatEnquiry = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => grannyFlatSchema.parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { error } = await supabaseAdmin.from("granny_flat_enquiries").insert({
      name: data.name,
      email: data.email,
      phone: data.phone || null,
      relationship: data.relationship,
      age_range: data.ageRange,
      funding_source: data.fundingSource,
      contribution_amount: data.contributionAmount || null,
      property_suburb: data.propertySuburb || null,
      meeting_preference: data.meetingPreference,
      message: data.message || null,
      source_page: data.sourcePage || null,
      ...attributionColumns(data),
    });

    if (error) {
      console.error("Failed to store granny flat enquiry", error);
      throw new Error("We could not send your enquiry. Please call us instead.");
    }

    const { sendLeadToCrm } = await import("@/lib/crm.server");
    await sendLeadToCrm({
      formType: "granny-flat-enquiry",
      name: data.name,
      email: data.email,
      phone: data.phone,
      matterType: "elder-law-granny-flat",
      sourcePage: data.sourcePage,
      message: data.message,
      details: {
        relationship: data.relationship,
        age_range: data.ageRange,
        funding_source: data.fundingSource,
        contribution_amount: data.contributionAmount,
        property_suburb: data.propertySuburb,
        meeting_preference: data.meetingPreference,
      },
    });

    return { ok: true } as const;
  });
